import { useContext } from 'react'

import { useAuthorizedMe } from '@/common/api/modules/auth'
import { useSiteOperatorDeleteApi, useSiteOperatorListApi } from '@/common/api/modules/siteOperator'
import { siteOperatorEditPath, siteOperatorNewPath } from '@/common/app/path'
import { useSiteId } from '@/common/app/site'
import { useShowDeletedToast } from '@/common/app/toast'
import { Operator } from '@/common/model/operator'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Icon, { IconColor, IconType } from '@/components/view/atoms/icon/Icon'
import Table from '@/components/view/atoms/table/Table'
import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import TableRow from '@/components/view/atoms/table/TableRow'
import TableRowItem from '@/components/view/atoms/table/TableRowItem'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'
import Content from '@/components/view/layouts/page/Content'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageListLoading from '@/components/view/molecules/loading/PageListLoading'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import { DeleteDialogContext } from '@/components/container/modal/dialog/delete/DeleteDialogProvider'

const SiteOperatorListPage = () => {
  const siteId = useSiteId()
  const { canManage } = useAuthorizedMe()
  const showDeleted = useShowDeletedToast()
  const { showModal, hide } = useContext(DeleteDialogContext)
  const { data, isLoading, mutate } = useSiteOperatorListApi({ site_id: siteId })
  const { trigger: deleteOperator } = useSiteOperatorDeleteApi({
    onSuccess: () => {
      hide()
      mutate()
    }
  })

  const onClickDelete = (operator: Operator) => {
    showModal({
      name: operator.name,
      onClickDelete: () =>
        deleteOperator({ site_id: siteId, operator_id: operator.id }).then(() =>
          showDeleted(operator.name)
        )
    })
  }

  const operators = data ?? []

  return (
    <Main type={MainLayoutType.SMALL} bottomSpace>
      <PageTitle
        title={'オペレーター管理'}
        description='サイト設定 — サイトのオペレーターを管理します'
        rightNode={
          canManage ? (
            <Button
              link={siteOperatorNewPath(siteId)}
              size='large'
              color={ButtonColor.PRIMARY_BRAND}
            >
              新規オペレーターを追加する
            </Button>
          ) : undefined
        }
      />
      <Content>
        {isLoading ? (
          <PageListLoading />
        ) : (
          <Area tableWrap noBorder>
            <Table>
              <thead>
                <TableRow head>
                  {[
                    '名前',
                    'メールアドレス',
                    '権限テンプレート',
                    ...(canManage ? ['操作'] : [])
                  ].map((label) => (
                    <TableHeadItem key={label}>
                      <Text>{label}</Text>
                    </TableHeadItem>
                  ))}
                </TableRow>
              </thead>
              <tbody>
                {operators.length === 0 ? (
                  <TableRow>
                    <TableRowItem>
                      <Text color={TextColor.SECONDARY}>オペレーターがいません</Text>
                    </TableRowItem>
                  </TableRow>
                ) : (
                  operators.map((op) => (
                    <TableRow key={op.id}>
                      <TableRowItem>
                        <Text>{op.name}</Text>
                      </TableRowItem>
                      <TableRowItem>
                        <Text>{op.email}</Text>
                      </TableRowItem>
                      <TableRowItem>
                        <Text color={op.permission_template_name ? undefined : TextColor.SECONDARY}>
                          {op.permission_template_name ?? '未設定'}
                        </Text>
                      </TableRowItem>
                      {canManage && (
                        <TableRowItem>
                          <ItemAlign gapX={8} align='center'>
                            <Button
                              link={siteOperatorEditPath(siteId, op.id)}
                              color={ButtonColor.SECONDARY}
                            >
                              編集する
                            </Button>
                            <Button
                              color={ButtonColor.SECONDARY}
                              square
                              onClick={() => onClickDelete(op)}
                            >
                              <Icon type={IconType.DELETE} color={IconColor.CRITICAL} line />
                            </Button>
                          </ItemAlign>
                        </TableRowItem>
                      )}
                    </TableRow>
                  ))
                )}
              </tbody>
            </Table>
          </Area>
        )}
      </Content>
    </Main>
  )
}

export default SiteOperatorListPage
